import React, { useState } from "react";
import axios from "axios";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const CATEGORY_OPTIONS = [
  { value: "gold", label: "22K Gold" },
  { value: "diamond", label: "Diamond" },
  { value: "silver", label: "Silver" },
  { value: "wedding-bands", label: "Wedding Bands" },
  { value: "bullion", label: "Gold Bullion & Coins" },
  { value: "lladro", label: "Lladro" },
];
const METAL_OPTIONS = ["22kt Gold", "24kt Gold", "18kt Gold", "Silver", "Diamond", "Platinum", "Other"];

const EMPTY = { title: "", price: "", category: "", metal: "", description: "", available: true, images: [] };

export function InventoryForm({ open, onOpenChange, product, auth, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(product ? {
        title: product.title || "",
        price: String(product.price ?? ""),
        category: (product.categories || [])[0] || "",
        metal: product.metal || "",
        description: product.description || "",
        available: product.available !== false,
        images: product.images || [],
      } : EMPTY);
    }
  }, [open, product]);

  const uploadPhotos = async (fileList) => {
    setUploading(true);
    for (const f of Array.from(fileList || [])) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: too large (max 8 MB)`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await axios.post(`${API}/admin/media/upload`, fd, auth);
        setForm((fm) => ({ ...fm, images: [...fm.images, `${API}/media/${res.data.id}`] }));
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please enter the product name");
    if (!product && form.images.length === 0) return toast.error("Please add at least one photo");
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      price: Number(form.price) || 0,
      category: form.category,
      metal: form.metal,
      description: form.description,
      available: form.available,
      images: form.images,
    };
    try {
      if (product) {
        await axios.patch(`${API}/admin/inventory/products/${product.handle}`, payload, auth);
        toast.success("Product updated — live on the site now");
      } else {
        await axios.post(`${API}/admin/inventory/products`, payload, auth);
        toast.success("Product added — live on the site now");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#e8e0d0] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inventory-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#171310]">{product ? "Edit Product" : "Add a Product"}</DialogTitle>
          <DialogDescription className="text-[#8f8571]">
            {product ? "Changes go live on the site immediately and are never overwritten by the sync." : "New products appear on the site immediately."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Product name</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 22K Gold Jhumka Earrings" className="rounded-none border-[#d8cdb5] h-11" data-testid="product-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Price (USD)</Label>
              <Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" className="rounded-none border-[#d8cdb5] h-11" data-testid="product-form-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-none border-[#d8cdb5] h-11 bg-white" data-testid="product-form-category"><SelectValue placeholder="Choose..." /></SelectTrigger>
                <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Metal / Karat</Label>
            <Select value={form.metal} onValueChange={(v) => setForm({ ...form, metal: v })}>
              <SelectTrigger className="rounded-none border-[#d8cdb5] h-11 bg-white" data-testid="product-form-metal"><SelectValue placeholder="Choose..." /></SelectTrigger>
              <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
                {METAL_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="A short description shoppers will see..." className="w-full border border-[#d8cdb5] bg-white px-3 py-2 text-sm outline-none focus:border-[#a8842c] transition-colors" data-testid="product-form-description" />
          </div>
          <div className="flex items-center justify-between border border-[#eee6d6] p-3">
            <Label>In stock</Label>
            <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="product-form-available" />
          </div>
          <div className="space-y-2">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2">
              {form.images.map((img) => (
                <div key={img} className="relative w-20 h-20 border border-[#eee6d6] bg-[#faf7f0]">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm({ ...form, images: form.images.filter((i) => i !== img) })} className="absolute -top-2 -right-2 bg-[#8c2f1b] text-white rounded-full p-0.5" aria-label="Remove photo">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className={`w-20 h-20 border-2 border-dashed border-[#d8cdb5] flex flex-col items-center justify-center cursor-pointer hover:border-[#a8842c] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                <ImagePlus size={18} className="text-[#a8842c]" />
                <span className="text-[9px] text-[#8f8571] mt-1">{uploading ? "..." : "Add"}</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} data-testid="product-form-photos" />
              </label>
            </div>
          </div>
          <Button onClick={save} disabled={saving || uploading} className="w-full rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-12 transition-colors duration-300" data-testid="product-form-save">
            {saving ? "Saving..." : product ? "Save Changes" : "Add Product"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
